<template>
    <MagTransition
        :name="transitionName"
        :enter-active-class="
            $style['transition_' + transitionName + '_enterActive']
        "
        :leave-active-class="
            $style['transition_' + transitionName + '_leaveActive']
        "
        :enter-from-class="
            $style['transition_' + transitionName + '_enterFrom']
        "
        :leave-to-class="$style['transition_' + transitionName + '_leaveTo']"
        :duration="transitionDuration"
        appear
        @after-leave="emit('closed')"
        @keyup.esc="emit('clickOutside')"
        @enter="emit('opening')"
        @after-enter="onOpened"
    >
        <Suspense>
            <FocusTrap
                :active="manualShowing != null ? manualShowing : showing"
                :return-focus-on-deactivate="!noReturnFocus"
                :preventScroll="true"
            >
                <div
                    v-show="manualShowing != null ? manualShowing : showing"
                    v-hotkey.global="keymap"
                    :class="[
                        $style.root,
                        {
                            [$style.drawer]: type === 'drawer',
                            [$style.dialog]:
                                type === 'dialog' || type === 'dialog:top',
                            [$style.popup]: type === 'popup',
                        },
                    ]"
                    :style="{
                        zIndex,
                        pointerEvents: (
                            manualShowing != null ? manualShowing : showing
                        )
                            ? 'auto'
                            : 'none',
                        '--transformOrigin': transformOrigin,
                    }"
                    tabindex="-1"
                >
                    <div
                        class="_modalBg data-cy-bg"
                        :class="[
                            $style.bg,
                            {
                                [$style.bgTransparent]: isEnableBgTransparent,
                                'data-cy-transparent': isEnableBgTransparent,
                            },
                        ]"
                        :style="{ zIndex }"
                        @contextmenu.prevent.stop="() => {}"
                    ></div>
                    <div
                        ref="content"
                        :class="[
                            $style.content,
                            {
                                [$style.fixed]: fixed,
                                top: type === 'dialog:top',
                            },
                        ]"
                        :style="{ zIndex }"
                    >
                        <slot :max-height="maxHeight" :type="type"></slot>
                    </div>
                </div>
            </FocusTrap>
        </Suspense>
    </MagTransition>
</template>

<script lang="ts" setup>
import {
    computed,
    nextTick,
    onMounted,
    provide,
    ref,
    useTemplateRef,
    watch,
} from "vue";
import { FocusTrap } from "focus-trap-vue";
import { onClickOutside, useResizeObserver } from "@vueuse/core";
import { useSettingsStore } from "@/store/settings-store";
import { storeToRefs } from "pinia";
import { refProp } from "@/composables/reactive";
import { useDeviceKind } from "@/composables/use-device-kind";
import { claimZIndex } from "@/composables/use-windows";

const settingsStore = useSettingsStore();
const { settings } = storeToRefs(settingsStore);
const disableDrawer = refProp(settings, "disableDrawer");

function getFixedContainer(el: Element | null): Element | null {
    if (el == null || el.tagName === "BODY") return null;
    const position = window.getComputedStyle(el).getPropertyValue("position");
    if (position === "fixed") {
        return el;
    } else {
        return getFixedContainer(el.parentElement);
    }
}

type ModalTypes = "popup" | "dialog" | "dialog:top" | "drawer";

const props = withDefaults(
    defineProps<{
        manualShowing?: boolean | null;
        anchor?: { x: string; y: string };
        src?: HTMLElement;
        preferType?: ModalTypes | "auto";
        zPriority?: "low" | "middle" | "high";
        noOverlap?: boolean;
        transparentBg?: boolean;
        noReturnFocus?: boolean;
    }>(),
    {
        manualShowing: null,
        src: null,
        anchor: () => ({ x: "center", y: "bottom" }),
        preferType: "auto",
        zPriority: "low",
        noOverlap: true,
        transparentBg: false,
        noReturnFocus: false,
    },
);

const emit = defineEmits<{
    (ev: "opening"): void;
    (ev: "opened"): void;
    (ev: "clickOutside"): void;
    (ev: "esc"): void;
    (ev: "close"): void;
    (ev: "closed"): void;
}>();

provide("modal", true);

const deviceKind = useDeviceKind();

const content = useTemplateRef("content");
const maxHeight = ref<number>();
const fixed = ref(false);
const transformOrigin = ref("center");
const showing = ref(true);
const zIndex = claimZIndex(props.zPriority);
const useSendAnime = ref(false);
const type = computed<ModalTypes>(() => {
    if (props.preferType === "auto") {
        if (!disableDrawer.value && deviceKind.value === "smartphone") {
            return "drawer";
        } else {
            return props.src != null ? "popup" : "dialog";
        }
    } else {
        return props.preferType!;
    }
});
const isEnableBgTransparent = computed(
    () => props.transparentBg && type.value === "popup",
);
let transitionName = computed(() =>
    useSendAnime.value
        ? "send"
        : type.value === "drawer"
          ? "modal-drawer"
          : type.value === "popup"
            ? "modal-popup"
            : "modal",
);
let transitionDuration = computed(() =>
    transitionName.value === "send"
        ? 400
        : transitionName.value === "modal-popup"
          ? 100
          : transitionName.value === "modal"
            ? 200
            : transitionName.value === "modal-drawer"
              ? 200
              : 0,
);

const focusedElement = document.activeElement;

function close(ev = undefined, opts: { useSendAnimation?: boolean } = {}) {
    // removeEventListener("popstate", close);
    // if (props.preferType == "dialog") {
    // 	history.forward();
    // }
    if (opts.useSendAnimation) {
        useSendAnime.value = true;
    }

    // eslint-disable-next-line vue/no-mutating-props
    if (props.src) props.src.style.pointerEvents = "auto";
    showing.value = false;
    emit("close");
    if (!props.noReturnFocus) {
        focusedElement?.focus({
            preventScroll: true,
        });
    }
}

onClickOutside(
    () => content.value?.firstElementChild,
    (e) => {
        const thisEl = content.value?.firstElementChild;
        const tgtEl = e.target;

        // DON'T trigger:
        // 1. on modals that aren't showing
        // 2. if root modal element is null
        // 3. if the click ends at the root modal element
        // 4. if the element target is a child or following neighbor in the tree
        const cond =
            (props.manualShowing != null
                ? props.manualShowing
                : showing.value) &&
            thisEl &&
            !document
                .elementsFromPoint(e.clientX, e.clientY)
                .includes(thisEl) &&
            tgtEl instanceof Element &&
            thisEl.compareDocumentPosition(tgtEl) &
                (Node.DOCUMENT_POSITION_CONTAINS |
                    Node.DOCUMENT_POSITION_PRECEDING);

        if (!cond) {
            return;
        }

        emit("clickOutside");
        e.stopPropagation();
    },
);

if (type.value === "drawer") {
    maxHeight.value = window.innerHeight / 1.5;
}

const keymap = {
    esc: () => emit("esc"),
};

const MARGIN = 16;

const align = () => {
    if (props.src == null) return;
    if (type.value === "drawer") return;
    if (type.value === "dialog") return;

    if (content.value == null) return;

    const srcRect = props.src.getBoundingClientRect();

    const width = content.value!.offsetWidth;
    const height = content.value!.offsetHeight;

    let left;
    let top;

    const x = srcRect.left + (fixed.value ? 0 : window.pageXOffset);
    const y = srcRect.top + (fixed.value ? 0 : window.pageYOffset);

    if (props.anchor.x === "center") {
        left = x + props.src.offsetWidth / 2 - width / 2;
    } else if (props.anchor.x === "left") {
        // TODO
    } else if (props.anchor.x === "right") {
        left = x + props.src.offsetWidth;
    }

    if (props.anchor.y === "center") {
        top = y - height / 2;
    } else if (props.anchor.y === "top") {
        // TODO
    } else if (props.anchor.y === "bottom") {
        top = y + props.src.offsetHeight;
    }

    if (fixed.value) {
        // 画面から横にはみ出る場合
        if (left + width > window.innerWidth) {
            left = window.innerWidth - width;
        }

        const underSpace = window.innerHeight - MARGIN - top;
        const upperSpace = srcRect.top - MARGIN;

        // 画面から縦にはみ出る場合
        if (top + height > window.innerHeight - MARGIN) {
            if (props.noOverlap && props.anchor.x === "center") {
                if (underSpace >= upperSpace / 3) {
                    maxHeight.value = underSpace;
                } else {
                    maxHeight.value = upperSpace;
                    top = upperSpace + MARGIN - height;
                }
            } else {
                top = window.innerHeight - MARGIN - height;
            }
        } else {
            maxHeight.value = underSpace;
        }
    } else {
        // 画面から横にはみ出る場合
        if (left + width - window.scrollX > window.innerWidth) {
            left = window.innerWidth - width + window.scrollX - 1;
        }

        const underSpace =
            window.innerHeight - MARGIN - (top - window.pageYOffset);
        const upperSpace = srcRect.top - MARGIN;

        // 画面から縦にはみ出る場合
        if (top + height - window.scrollY > window.innerHeight - MARGIN) {
            if (props.noOverlap && props.anchor.x === "center") {
                if (underSpace >= upperSpace / 3) {
                    maxHeight.value = underSpace;
                } else {
                    maxHeight.value = upperSpace;
                    top = window.scrollY + (upperSpace + MARGIN - height);
                }
            } else {
                top =
                    window.innerHeight -
                    MARGIN -
                    height +
                    window.pageYOffset -
                    1;
            }
        } else {
            maxHeight.value = underSpace;
        }
    }

    if (top < 0) {
        top = MARGIN;
    }

    if (left < 0) {
        left = 0;
    }

    let transformOriginX = "center";
    let transformOriginY = "center";

    if (
        top >=
        srcRect.top +
            props.src.offsetHeight +
            (fixed.value ? 0 : window.pageYOffset)
    ) {
        transformOriginY = "top";
    } else if (
        top + height <=
        srcRect.top + (fixed.value ? 0 : window.pageYOffset)
    ) {
        transformOriginY = "bottom";
    }

    if (
        left >=
        srcRect.left +
            props.src.offsetWidth +
            (fixed.value ? 0 : window.pageXOffset)
    ) {
        transformOriginX = "left";
    } else if (
        left + width <=
        srcRect.left + (fixed.value ? 0 : window.pageXOffset)
    ) {
        transformOriginX = "right";
    }

    transformOrigin.value = `${transformOriginX} ${transformOriginY}`;

    content.value.style.left = left + "px";
    content.value.style.top = top + "px";
};

const onOpened = () => {
    emit("opened");
};

useResizeObserver(content, (entries, observer) => {
    requestAnimationFrame(() => {
        align();
    });
});

onMounted(() => {
    watch(
        () => props.src,
        async () => {
            if (props.src) {
                // eslint-disable-next-line vue/no-mutating-props
                props.src.style.pointerEvents = "none";
            }
            fixed.value =
                type.value === "drawer" || getFixedContainer(props.src) != null;

            await nextTick();

            align();
        },
        { immediate: true },
    );
});

defineExpose({
    close,
});
</script>

<style lang="scss" module>
.transition_send_enterActive,
.transition_send_leaveActive {
    > .bg {
        transition: opacity 0.3s !important;
    }

    > .content {
        transform: translateY(0px);
        transition:
            opacity 0.3s ease-in,
            transform 0.3s cubic-bezier(0.5, -0.5, 1, 0.5) !important;
    }
}

.transition_send_enterFrom,
.transition_send_leaveTo {
    > .bg {
        opacity: 0;
    }

    > .content {
        pointer-events: none;
        opacity: 0;
        transform: translateY(-300px);
    }
}

.transition_modal_enterActive,
.transition_modal_leaveActive {
    > .bg {
        transition: opacity 0.2s !important;
    }

    > .content {
        transform-origin: var(--transformOrigin);
        transition:
            opacity 0.2s,
            transform 0.2s !important;
    }
}

.transition_modal_enterFrom,
.transition_modal_leaveTo {
    > .bg {
        opacity: 0;
    }

    > .content {
        pointer-events: none;
        opacity: 0;
        transform-origin: var(--transformOrigin);
        transform: scale(0.9);
    }
}

.transition_modal-popup_enterActive,
.transition_modal-popup_leaveActive {
    > .bg {
        transition: opacity 0.2s !important;
    }

    > .content {
        transform-origin: var(--transformOrigin);
        transition:
            opacity 0.2s cubic-bezier(0, 0, 0.2, 1),
            transform 0.2s cubic-bezier(0, 0, 0.2, 1) !important;
    }
}

.transition_modal-popup_enterFrom,
.transition_modal-popup_leaveTo {
    > .bg {
        opacity: 0;
    }

    > .content {
        pointer-events: none;
        opacity: 0;
        transform-origin: var(--transformOrigin);
        transform: scale(0.9);
    }
}

.transition_modal-drawer_enterActive {
    > .bg {
        transition: opacity 0.2s !important;
    }

    > .content {
        transition: transform 0.2s cubic-bezier(0, 0.5, 0, 1) !important;
    }
}

.transition_modal-drawer_leaveActive {
    > .bg {
        transition: opacity 0.2s !important;
    }

    > .content {
        transition: transform 0.2s cubic-bezier(0, 0.5, 0, 1) !important;
    }
}

.transition_modal-drawer_enterFrom,
.transition_modal-drawer_leaveTo {
    > .bg {
        opacity: 0;
    }

    > .content {
        pointer-events: none;
        transform: translateY(100%);
    }
}

.root {
    outline: none;

    &.dialog {
        > .content {
            position: fixed;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            margin: auto;
            padding: 32px;
            mask-image: linear-gradient(
                0deg,
                rgba(0, 0, 0, 0) 0%,
                rgba(0, 0, 0, 1) 32px,
                rgba(0, 0, 0, 1) calc(100% - 32px),
                rgba(0, 0, 0, 0) 100%
            );
            overflow: auto;
            display: flex;

            @media (max-width: 500px) {
                padding: 16px;
                mask-image: linear-gradient(
                    0deg,
                    rgba(0, 0, 0, 0) 0%,
                    rgba(0, 0, 0, 1) 16px,
                    rgba(0, 0, 0, 1) calc(100% - 16px),
                    rgba(0, 0, 0, 0) 100%
                );
            }

            > ::v-deep(*) {
                margin: auto;
            }

            &.top {
                > ::v-deep(*) {
                    margin-top: 0;
                }
            }
        }
    }

    &.popup {
        > .content {
            position: absolute;

            &.fixed {
                position: fixed;
            }
        }
    }

    &.drawer {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        overflow: clip;

        > .content {
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            margin: auto;

            > ::v-deep(*) {
                margin: auto;
            }
        }
    }
}

.bg {
    &.bgTransparent {
        background: transparent;
        -webkit-backdrop-filter: none;
        backdrop-filter: none;
    }
}
</style>
